import { zodResolver } from "@hookform/resolvers/zod";
import { KikUserZodSchemas } from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import { useTranslations } from "next-intl";
import { ReactElement, ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useAuthContext } from "~/contexts/auth";
import { useMagicLinkSignalr } from "~/hooks/use-signalr";
import Logger from "~/services/logger/logger";
import { SignalRAction } from "~/shared/signalr-action.enum";

import Button from "../common/button";
import InputField from "../form/input-field";
import styles from "./send-magic-link.module.scss";

const trackOrderSchema = z.object({
  orderId: z.string({
    required_error: "error.missing_required_field",
    invalid_type_error: "error.missing_required_field",
  }),
  email: KikUserZodSchemas.email,
});

type Fields = z.infer<typeof trackOrderSchema>;

type Props = {
  title: string;
  desc: string;
  desc2: string | ReactElement | ReactNode | ReactNode[];
  submit: string;
  action: SignalRAction;
  isRequestReturn?: boolean;
};

export default function SendMagicLink(props: Props) {
  const t = useTranslations();
  const [isConfirmed, setConfirmed] = useState(false);
  const { onSignalRLogin } = useAuthContext();

  const [customError, setCustomError] = useState(false);

  const { sendMagicLink } = useMagicLinkSignalr({
    action: props.action,
    onLoginResponse: async (r) => setConfirmed(onSignalRLogin(r)),
  });

  const { formState, handleSubmit, register, clearErrors } = useForm<Fields>({
    resolver: zodResolver(trackOrderSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
  });

  const onSubmit = async (data: Fields) => {
    setCustomError(false);
    Logger.instance.info("on submit", data);
    // async request which may result error
    try {
      await sendMagicLink(data.email, data.orderId);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (e) {
      // handle your error
      Logger.instance.info("error message :", (e as Error).message);
      setCustomError(true);
      // throw new Error((e as Error).message);
    }
  };

  return (
    <div className={styles.trackBox}>
      {formState.isSubmitSuccessful && !customError ? (
        isConfirmed ? (
          <>
            <h1 className={styles.title}>{t("generic.magicLink.confirmed.title")}</h1>
            <p className={styles.centered}>{t("generic.magicLink.confirmed.description")}</p>
          </>
        ) : (
          <>
            <h1 className={styles.title}>{t("generic.checkYourEmail")}</h1>
            <p className={styles.centered}>{t("generic.checkYourEmailDesc")}</p>
          </>
        )
      ) : (
        <>
          <h1 className={styles.title}>{props.title}</h1>
          <p>{props.desc}</p>
          <p>{props.desc2}</p>
          <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.inputs}>
              <InputField
                type={"number"}
                {...register("orderId")}
                label={t("generic.orderN")}
                error={formState.errors?.orderId ? t(formState.errors.orderId.message) : undefined}
                onClearErrors={() => clearErrors("orderId")}
                customStyles={{ inputField: styles.initialAppeareance }}
              />
              <InputField
                type={"email"}
                {...register("email")}
                label={t("generic.form.EmailAddress")}
                error={formState.errors?.email ? t(formState.errors.email.message) : undefined}
                onClearErrors={() => clearErrors("email")}
              />
            </div>
            <p className={styles.mandatoryLabel}>{t("generic.form.mandatory_long")}</p>
            {customError && <p className={styles.errorMessage}>{t("error.magicLink")}</p>}
            <Button type="submit" variant="primary" disabled={formState.isSubmitting} loading={formState.isSubmitting}>
              {props.submit}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
